import { useQuery } from '@apollo/client';
import {
  fetchInvoiceQuery,
  InvoiceDetail,
  getInvoiceIcon,
  getInvoiceSubheader,
} from '../../../account-subscriptions/billing';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { InvoiceWithLineItems } from '@atlassian/cssxp-types-billing';
import { PageHeader, ErrorComponents } from '../..';
import { withApollo } from '../../graphql-provider';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { generatedTypesNew } from '@css-xp/graphtypes';

interface Props {
  invoiceId: string;
}

export const InvoicePreview = React.memo(({ invoiceId }: Props) => {
  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchInvoiceQueryQuery,
    generatedTypesNew.FetchInvoiceQueryQueryVariables
  >(fetchInvoiceQuery, {
    variables: {
      id: invoiceId,
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorComponents.GenericErrorPage error={error} />;
  }

  const invoice = data?.invoice as InvoiceWithLineItems;
  if (!invoice) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find invoice with ID: ${invoiceId}`)}
      />
    );
  }

  if (!invoice?.lineItems) {
    return null;
  }

  const { id, sourceSystem, invoiceNumber } = invoice;

  const identifier = (sourceSystem === SourceSystem.CCP ? invoiceNumber : id) ?? id;

  return (
    <div className="full-entity billing-document">
      <PageHeader
        icon={getInvoiceIcon()}
        header={<Link to={`/invoices/${encodeURIComponent(identifier)}`}>{identifier}</Link>}
        subheader={getInvoiceSubheader(invoice)}
      />
      <InvoiceDetail invoice={invoice} />
    </div>
  );
});

export default withApollo(InvoicePreview);
